import React, { useEffect, useState, useCallback } from "react";
import { Button, Card, Row, Col, Badge, Form, Alert } from "react-bootstrap";
import axios from "axios";
import Layout from "../../components/layouts/Main";
import config from "../../config";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { PulseLoader } from "react-spinners";
import { useHasPermission, PERMISSIONS } from "../../utils/permissions";
import PermissionGate from "../../components/common/PermissionGate";

// Tab Components
import PODetailsTab from "../../components/purchaseOrders/PODetailsTab";
import GRNTab from "../../components/purchaseOrders/GRNTab";
// import QualityUnloadingTab from "../../components/purchaseOrders/QualityUnloadingTab";
import PurchaseOrderPayments from "../../components/purchaseOrders/PaymentTab";
import PurchaseOrderDocumentsPage from "../../components/purchaseOrders/PurchaseOrderDocumentsPage";
import QualityCheckTab from "../../components/purchaseOrders/QualityCheckTab";
import UnloadingTab from "../../components/purchaseOrders/UnloadingTab";

import ReceiveReturnTab from "../../components/purchaseOrders/ReceiveReturnTab";
import PDFDownloadButton from "../../components/common/PDFDownloadButton";
import ConfirmationModal from "../../components/common/ConfirmationModal";

export default function PurchaseOrderDetails() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [vendor, setVendor] = useState(null);
  const [quotation, setQuotation] = useState(null);
  const [broker, setBroker] = useState(null);
  const [items, setItems] = useState([]);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || "po-details");
  const [submitting, setSubmitting] = useState(false);

  // Modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmConfig, setConfirmConfig] = useState({
    title: '',
    message: '',
    confirmVariant: 'primary',
    confirmText: 'Confirm',
    onConfirm: () => { }
  });

  const openConfirm = (config) => {
    setConfirmConfig(config);
    setShowConfirmModal(true);
  };

  // Permission checks
  const canViewPO = useHasPermission(PERMISSIONS.PO_VIEW);
  const canEditPO = useHasPermission(PERMISSIONS.PO_EDIT);
  const canDeletePO = useHasPermission(PERMISSIONS.PO_DELETE);
  const canViewGRN = useHasPermission(PERMISSIONS.GRN_CREATE);
  const canViewQuality = useHasPermission(PERMISSIONS.QUALITY_CHECK_CREATE);
  const canViewUnloading = useHasPermission(PERMISSIONS.UNLOADING_ENTRY_ADD);
  const canViewReturns = useHasPermission(PERMISSIONS.RETURN_NOTE_CREATE);
  const canViewDocuments = useHasPermission(PERMISSIONS.PO_DOC_ADD);
  const canViewPayments = useHasPermission(PERMISSIONS.PO_PAYMENT_ADD);

  const fetchOrder = useCallback(async () => {
    if (!canViewPO) {
      toast.error("You don't have permission to view purchase order details");
      return;
    }

    try {
      const res = await axios.get(
        `${config.API_BASE_URL}/purchase-orders/${id}`
      );
      setOrder(res.data.order);
      setVendor(res.data.vendor);
      setQuotation(res.data?.quotation);
      setBroker(res.data?.broker);

      const itemsRes = await axios.get(
        `${config.API_BASE_URL}/purchase-order-items?purchaseOrderId=${id}`
      );
      setItems(itemsRes.data);
    } catch (err) {
      console.error("Error fetching purchase order:", err);
      toast.error("Error fetching purchase order details");
    }
  }, [canViewPO, id]);

  useEffect(() => {
    fetchOrder();
  }, [canViewPO, fetchOrder]);

  const getStatusBadge = (status) => {
    const map = {
      Draft: "secondary",
      Approved: "info",
      "In Transit": "info",
      "Quality Check": "warning",
      Completed: "success",
      Created: "secondary",
      Cancelled: "danger",
    };
    return <Badge bg={map[status] || "dark"}>{status}</Badge>;
  };

  const getPaymentBadge = (status) => {
    const map = {
      Unpaid: "danger",
      Partial: "warning",
      Paid: "success",
    };
    return <Badge bg={map[status] || "secondary"}>{status}</Badge>;
  };

  const handleStatusChange = async (newStatus) => {
    if (!canEditPO) {
      toast.error("You don't have permission to update purchase order status");
      return;
    }

    setSubmitting(true);
    try {
      await axios.put(`${config.API_BASE_URL}/purchase-orders/${id}`, {
        status: newStatus
      });
      fetchOrder();
      toast.success("Status updated successfully");
    } catch (err) {
      console.error("Error updating status:", err);
      toast.error("Error updating status");
    } finally {
      setSubmitting(false);
      setShowConfirmModal(false);
    }
  };

  const handleApprove = async () => {
    if (!canEditPO) {
      toast.error("You don't have permission to approve purchase orders");
      return;
    }

    setSubmitting(true);
    try {
      await axios.put(`${config.API_BASE_URL}/purchase-orders/${id}/approve`);
      fetchOrder();
      toast.success("Purchase order approved successfully!");
    } catch (err) {
      console.error("Error approving PO:", err);
      toast.error(err.response?.data?.message || "Failed to approve purchase order");
    } finally {
      setSubmitting(false);
      setShowConfirmModal(false);
    }
  };

  const handleCancel = async () => {
    if (!canEditPO) {
      toast.error("You don't have permission to cancel purchase orders");
      return;
    }

    setSubmitting(true);
    try {
      await axios.put(`${config.API_BASE_URL}/purchase-orders/${id}/cancel`);
      fetchOrder();
      toast.success("Purchase order cancelled");
    } catch (err) {
      console.error("Error cancelling PO:", err);
      toast.error("Failed to cancel purchase order");
    } finally {
      setSubmitting(false);
      setShowConfirmModal(false);
    }
  };

  // If user can't view PO, show permission denied
  if (!canViewPO) {
    return (
      <Layout>
        <nav aria-label="breadcrumb" className="mb-3">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/purchase-orders">Purchase Orders</Link>
            </li>
            <li className="breadcrumb-item active">Access Denied</li>
          </ol>
        </nav>
        <Alert variant="danger" className="d-flex align-items-center">
          <i className="fas fa-exclamation-triangle me-2"></i>
          <div>
            <strong>Access Denied</strong>
            <p className="mb-0">You don't have permission to view purchase order details. Contact your administrator for access.</p>
          </div>
        </Alert>
      </Layout>
    );
  }

  if (!order) {
    return (
      <Layout>
        <div className="text-center py-4">
          <PulseLoader color="#014b7cbb" size={9} />
        </div>
      </Layout>
    );
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case "po-details":
        return <PODetailsTab order={order} items={items} />;
      case "grn":
        return canViewGRN ? <GRNTab purchaseOrderId={order._id} onUpdate={fetchOrder} /> : null;
      case "quality":
        return canViewQuality ? <QualityCheckTab purchaseOrderId={order._id} items={items} onUpdate={fetchOrder} orderStatus={order.status} /> : null;
      case "unloading":
        return canViewUnloading ? <UnloadingTab purchaseOrderId={order._id} items={items} onUpdate={fetchOrder} /> : null;
      case "recieve-return-notes":
        return canViewReturns ? <ReceiveReturnTab purchaseOrderId={order._id} purchaseOrderVendorId={order.vendorId} onUpdate={fetchOrder} /> : null;
      case "order-docs":
        return canViewDocuments ? <PurchaseOrderDocumentsPage purchaseOrderId={order._id} /> : null;
      case "po-payments":
        return canViewPayments ? <PurchaseOrderPayments purchaseOrderId={order._id} /> : null;
      default:
        return <PODetailsTab order={order} items={items} />;
    }
  };

  return (
    <Layout>
      <nav aria-label="breadcrumb" className="mb-3">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <Link to="/purchase-orders">Purchase Orders</Link>
          </li>
          <li className="breadcrumb-item active">#{order.orderId || 'Pending'}</li>
        </ol>
      </nav>

      <Row className="mb-3">
        {/* Top Summary Card */}
        <Card className="mb-4 shadow-sm border-0">
          <Card.Body>
            <Row className="align-items-center">
              <Col md={12} className="d-flex gap-2">
                <h5 className="mb-2 text-primary">
                  <i className="fas fa-file-invoice me-2"></i>
                  Order #{order.orderId || 'Pending'}
                </h5>
                <p className="small text-muted fw-semibold">(
                  {items.map((it, idx) => (
                    <span key={it._id || idx}>
                      {it.productId?.productName} {it.orderedQuantity}{it.unit}{idx < items.length - 1 ? ', ' : ''}
                    </span>
                  ))}
                  )
                </p>
              </Col>
              {/* Left Section */}
              <Col md={4}>

                <p className="mb-1">
                  <i className="bi bi-person-badge-fill me-1"></i>
                  <strong>Vendor:</strong> {vendor?.business_name || "—"}
                </p>
                {quotation && (
                  <p className="mb-1">
                    <i className="bi bi-file-earmark-text-fill me-1"></i>
                    <strong>Quotation:</strong> {quotation?.quotationId}
                  </p>
                )}
                {broker && (
                  <p className="mb-1">
                    <i className="bi bi-people-fill me-1"></i>
                    <strong>via:</strong> {broker?.business_name}
                  </p>
                )}
                <p className="mb-0 text-muted">
                  <i className="fas fa-calendar me-1"></i>
                  <small>Created: {new Date(order.orderDate).toLocaleDateString()}</small>
                </p>
              </Col>

              {/* Middle Section */}
              <Col md={4}>
                <div className="mb-2">
                  <strong>Status:</strong>
                  <PermissionGate
                    require={PERMISSIONS.PO_EDIT}
                    fallback={
                      <span className="ms-2">
                        {getStatusBadge(order.status)}
                      </span>
                    }
                  >
                    {order.status === 'Draft' || order.status === 'Cancelled' ? (
                      <span className="ms-2">
                        {getStatusBadge(order.status)}
                      </span>
                    ) : (
                      <>
                      {getStatusBadge(order.status)}
                      {/* <Form.Select
                        size="sm"
                        style={{
                          width: "auto",
                          display: "inline-block",
                          marginLeft: "10px",
                        }}
                        value={order.status}
                        onChange={(e) => handleStatusChange(e.target.value)}
                      >
                        <option value="Approved">Approved</option>
                        <option value="Created">Created</option>
                        <option value="Partial Received">Partial Received</option>
                        <option value="Fully Received">Fully Received</option>
                        <option value="Closed">Closed</option>
                        <option value="Cancelled">Cancelled</option>
                      </Form.Select> */}
                      </>
                    )}
                  </PermissionGate>
                </div>
                {order.status !== 'Draft' && order.status !== 'Cancelled' && (
                  <p className="mb-0">
                    <strong>Payment:</strong>{" "}
                    {getPaymentBadge(order.paymentStatus)}
                  </p>
                )}
                {order.itemsTotalPrice && (
                  <p className="mb-0 mt-1">
                    <strong>Total:</strong>{" "}
                    <span className="text-success">₹{parseFloat(order.itemsTotalPrice).toFixed(2)}</span>
                  </p>
                )}
              </Col>

              {/* Right Section */}
              <Col md={4} className="text-md-end mt-3 mt-md-0">
                <div className="d-flex flex-wrap gap-2 justify-content-md-end">
                  {order.status === 'Draft' && canEditPO && (
                    <>
                      <Button
                        variant="success"
                        size="sm"
                        onClick={() => openConfirm({
                          title: 'Approve Purchase Order',
                          message: 'Are you sure you want to approve this purchase order? This will generate a formal PO Number.',
                          confirmVariant: 'success',
                          confirmText: 'Approve',
                          onConfirm: handleApprove
                        })}
                        title="Confirm and Approve PO"
                      >
                        <i className="fas fa-check-circle me-1"></i> Approve
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => openConfirm({
                          title: 'Cancel Purchase Order',
                          message: 'Are you sure you want to cancel/reject this purchase order?',
                          confirmVariant: 'danger',
                          confirmText: 'Cancel Order',
                          onConfirm: handleCancel
                        })}
                        title="Cancel/Reject PO"
                      >
                        <i className="fas fa-times-circle me-1"></i> Cancel/Reject
                      </Button>
                    </>
                  )}

                  <PermissionGate
                    require={PERMISSIONS.PO_EDIT}
                    fallback={
                      <Button variant="outline-secondary" disabled size="sm" title="Edit access restricted">
                        <i className="bi bi-pencil-square me-1"></i> Edit
                      </Button>
                    }
                  >
                    {order.status !== 'Cancelled' && (
                      <Link
                        className="btn btn-outline-warning btn-sm"
                        to={`/edit-purchase-order/${order._id}`}
                      >
                        <i className="bi bi-pencil-square me-1"></i> Edit
                      </Link>
                    )}
                  </PermissionGate>

                  {order.status !== 'Draft' && order.status !== 'Cancelled' && (
                    <PDFDownloadButton
                      documentType="purchase-order"  // or 'sales-order' or 'dispatch-note'
                      documentId={order._id}
                      documentNumber={order.orderId}
                      variant="success"
                      size="sm"
                      label="PDF"
                    />
                  )}

                  {/* Hide delete button as requested */}
                  {/* <PermissionGate require={PERMISSIONS.PO_DELETE}>
                    <Button variant="outline-danger" size="sm" title="Delete Purchase Order">
                      <i className="fas fa-trash me-1"></i> Delete
                    </Button>
                  </PermissionGate> */}
                </div>

                {(!canEditPO && !canDeletePO) && (
                  <small className="text-muted d-block mt-1">
                    <i className="fas fa-eye me-1"></i>Read-only access
                  </small>
                )}
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Tab Navigation and Content */}
        <Card>
          <Card.Header>
            {/* Custom Tab Navigation */}
            <ul className="nav nav-tabs card-header-tabs">
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === "po-details" ? "active" : ""}`}
                  onClick={() => setActiveTab("po-details")}
                >
                  <i className="fas fa-info-circle me-1"></i>
                  PO Details
                </button>
              </li>

              {order.status !== 'Draft' && !['Cancelled'].includes(order.status) && (
                <>
                  {canViewGRN && (
                    <li className="nav-item">
                      <button
                        className={`nav-link ${activeTab === "grn" ? "active" : ""}`}
                        onClick={() => setActiveTab("grn")}
                      >
                        <i className="fas fa-truck me-1"></i>
                        GRN (Inward)
                      </button>
                    </li>
                  )}

                  {canViewQuality && (
                    <li className="nav-item">
                      <button
                        className={`nav-link ${activeTab === "quality" ? "active" : ""}`}
                        onClick={() => setActiveTab("quality")}
                      >
                        <i className="fas fa-check-circle me-1"></i>
                        Quality Check
                      </button>
                    </li>
                  )}

                  {canViewUnloading && (
                    <li className="nav-item">
                      <button
                        className={`nav-link ${activeTab === "unloading" ? "active" : ""}`}
                        onClick={() => setActiveTab("unloading")}
                      >
                        <i className="fas fa-dolly me-1"></i>
                        Unloading
                      </button>
                    </li>
                  )}

                  {canViewReturns && (
                    <li className="nav-item">
                      <button
                        className={`nav-link ${activeTab === "recieve-return-notes" ? "active" : ""}`}
                        onClick={() => setActiveTab("recieve-return-notes")}
                      >
                        <i className="fas fa-undo me-1"></i>
                        Receive & Return Note
                      </button>
                    </li>
                  )}

                  {canViewDocuments && (
                    <li className="nav-item">
                      <button
                        className={`nav-link ${activeTab === "order-docs" ? "active" : ""}`}
                        onClick={() => setActiveTab("order-docs")}
                      >
                        <i className="fas fa-file-alt me-1"></i>
                        Documents
                      </button>
                    </li>
                  )}

                  {canViewPayments && (
                    <li className="nav-item">
                      <button
                        className={`nav-link ${activeTab === "po-payments" ? "active" : ""}`}
                        onClick={() => setActiveTab("po-payments")}
                      >
                        <i className="fas fa-credit-card me-1"></i>
                        Payments
                      </button>
                    </li>
                  )}
                </>
              )}
            </ul>
          </Card.Header>

          <Card.Body>
            {renderTabContent()}

            {/* Show message if user has limited access to tabs */}
            {!canViewGRN && !canViewQuality && !canViewUnloading && !canViewReturns && !canViewDocuments && !canViewPayments && activeTab !== "po-details" && (
              <Alert variant="info" className="mt-3">
                <i className="fas fa-info-circle me-2"></i>
                <strong>Limited Access</strong>
                <p className="mb-0">
                  You have read-only access to this purchase order. Additional tabs require specific permissions:
                </p>
                <ul className="mt-2 mb-0">
                  <li>GRN (Inward) - requires GRN creation permission</li>
                  <li>Quality Check - requires quality check permission</li>
                  <li>Unloading - requires unloading entry permission</li>
                  <li>Returns - requires return note creation permission</li>
                  <li>Documents - requires document management permission</li>
                  <li>Payments - requires payment management permission</li>
                </ul>
              </Alert>
            )}
          </Card.Body>
        </Card>
      </Row>

      <ConfirmationModal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        onConfirm={confirmConfig.onConfirm}
        title={confirmConfig.title}
        message={confirmConfig.message}
        confirmText={confirmConfig.confirmText}
        confirmVariant={confirmConfig.confirmVariant}
        isLoading={submitting}
      />
    </Layout >
  );
}
